import { useMutation, useQueryClient } from "@tanstack/react-query";

import { translate } from "app/filters/i18nFilter";
import { ERROR_CREATE_RT_TEXT } from "app/pages/activation/components/RankWidget/texts";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { ACTIVATION_OBJECT_QUERY_KEY } from "app/pages/activation/queries/data/useActivationObjectQuery";
import { TRACKER_CAMPAIGNS_QUERY_KEY } from "app/pages/activation/queries/rankTracker/useGetRankTrackerCampaignsQuery";
import { TOPICS_QUERY_KEY } from "app/pages/activation/queries/topics/useGetTopicsDataQuery";
import { createActivationApi } from "app/pages/activation/services/api";
import type { RTCampaignCreationDTO } from "app/pages/activation/types/api";

import type { BenchmarkGranularity } from "../../types/common";

const CREATE_RT_CAMPAIGN_MUTATION_KEY = "CREATE_RT_CAMPAIGN_MUTATION_KEY";

export const useCreateRTCampaignMutation = (activationId: string, granularity: BenchmarkGranularity) => {
    const { showErrorToast } = useToasts();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [CREATE_RT_CAMPAIGN_MUTATION_KEY],
        mutationFn: async ({ projectId, creationData }: { projectId: string; creationData: RTCampaignCreationDTO }) => {
            const { rankTracker } = createActivationApi();

            return await rankTracker.createCampaign(creationData, projectId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ACTIVATION_OBJECT_QUERY_KEY, activationId] });
            queryClient.invalidateQueries({ queryKey: [TRACKER_CAMPAIGNS_QUERY_KEY] });
        },
        onError: () => {
            showErrorToast(translate(ERROR_CREATE_RT_TEXT));
            queryClient.invalidateQueries({ queryKey: [TOPICS_QUERY_KEY, granularity, activationId] });
            queryClient.invalidateQueries({ queryKey: [ACTIVATION_OBJECT_QUERY_KEY, activationId] });
        },
    });
};
